import { useCallback } from 'react'
import {
  useMutationUpdateOrder,
  useMutationAddToCurrentOrder,
} from '@/features/shop/services/Orders/mutations'
import {
  useMutationAddToCart,
  useMutationUpdateCart,
} from '@/features/shop/services/Cart/mutations'
import { useActiveOrder } from '@/features/shop/state/SelectedActiveOrder/hooks'
import { type MutationProduct } from '@shared/types'
import { useShowToast } from '@/features/shared/state/Toast/hooks'
import { ToastType } from '@/features/shared/constants/toastTypes'

type UpdateArg = {
  itemUpdated: boolean
  product: MutationProduct
  quantity: number
  showToastOnError?: boolean
}
export type UpdateProductQuantityFn = ReturnType<
  typeof useUpdateProductQuantity
>['updateProductQuantity']
export const useUpdateProductQuantity = () => {
  const currentActiveOrder = useActiveOrder()
  const { showToast } = useShowToast()
  const { mutateAsync: addToCart, error: addToCartError } = useMutationAddToCart()
  const { mutateAsync: updateCart, error: updateCartError } = useMutationUpdateCart()

  const { mutateAsync: updateOrderFetchProgress, error: updateOrderError } =
    useMutationUpdateOrder()
  const { mutateAsync: addToCurrentOrderFetchProgress, error: addToCurrentOrderError } =
    useMutationAddToCurrentOrder()

  const updateProductError =
    updateOrderError || addToCurrentOrderError || addToCartError || updateCartError

  const updateProductQuantity = useCallback(
    async ({ product, quantity = 0, itemUpdated = false, showToastOnError = true }: UpdateArg) => {
      try {
        const productForUpdate = { ...product, quantity }
        if (currentActiveOrder?.id) {
          itemUpdated
            ? await updateOrderFetchProgress({
                order: currentActiveOrder,
                products: { [productForUpdate.id]: productForUpdate },
              })
            : await addToCurrentOrderFetchProgress([productForUpdate])
        } else {
          itemUpdated ? await updateCart(productForUpdate) : await addToCart([productForUpdate])
        }
      } catch (e) {
        // we want to show an inline error in some uses of this hook (e.g. PDP)
        if (showToastOnError) {
          const message = `Error updating your ${currentActiveOrder?.id ? 'order' : 'cart'}`
          showToast(ToastType.ICON_TOAST, {
            ariaLabel: message,
            props: {
              title: message,
              iconType: 'alert',
            },
          })
        }
      }
    },
    [
      currentActiveOrder,
      updateOrderFetchProgress,
      addToCurrentOrderFetchProgress,
      updateCart,
      addToCart,
      showToast,
    ],
  )

  return {
    updateProductQuantity,
    updateProductError,
  }
}
